import React from 'react';
import { useTranslation } from 'services/localization';
import { AccountHolderApi } from 'common/types';
import { PaymentOnboardButton } from './PaymentOnboardButton';
import Banner from 'components/Banner';

interface Props {
	account: AccountHolderApi;
}

type AlertType = 'NEEDS_INFO' | 'PENDING_INFO';

export const ActionRequiredBanner = ({ account }: Props) => {
	const { t } = useTranslation();
	const { verification } = account;
	const companyVerification = verification.accountHolder;
	const shareholdersVerification = verification.shareholders;
	const bankAccountsVerification = verification.bankAccounts;
	const verificationChecks = [
		...(companyVerification.checks || []),
		...(shareholdersVerification || []).map((sh) => sh.checks).flat(),
		...(bankAccountsVerification || []).map((ba) => ba.checks).flat(),
	];
	const awaitingOrInvalidData = !!verificationChecks.filter(
		(check) =>
			check.status === 'AWAITING_DATA' ||
			check.status === 'INVALID_DATA' ||
			check.status === 'FAILED',
	).length;
	const pendingData = !!verificationChecks.filter((check) => check.status === 'PENDING').length;

	const alertTexts: Record<AlertType, { title: string; body: string }> = {
		NEEDS_INFO: {
			title: t('common:prompts.actionRequired', 'Action required'),
			body: t(
				'account.payments.paymentInfoNeeded',
				'We need more information to complete your payment account',
			),
		},
		PENDING_INFO: {
			title: t('common:prompts.verificationInProgress', 'Verification in progress'),
			body: t('common:prompts.verifyingInformation', 'We are verifying your information'),
		},
	};

	const alertType: AlertType | null = awaitingOrInvalidData
		? 'NEEDS_INFO'
		: pendingData
		? 'PENDING_INFO'
		: null;

	if (!alertType) {
		return null;
	}

	return (
		<Banner
			variant="warning"
			title={alertTexts[alertType].title}
			description={alertTexts[alertType].body}
			buttonEl={
				<PaymentOnboardButton
					text={
						awaitingOrInvalidData
							? t('settings.completeSetup', 'Complete setup')
							: t('settings.seeCompanyInfo', 'View information')
					}
					accountHolderCode={account.accountHolderCode}
					variant="textPrimary"
				/>
			}
		/>
	);
};
